import { istFormel, zerlege } from "./formelzerleger";
import { schreibBezug } from "./zellbezug";
import { FEHLER } from "./formelrechner";

/*
  Formeln umschreiben, wenn sich das Blatt verändert.

  ---------------------------------------------------------------
  Warum das überhaupt sein muss
  ---------------------------------------------------------------

  Zeilen und Spalten haben im Modell stabile Zufallskennungen — nur so
  überleben Kommentare und das Umsortieren. Formeln benutzen aber
  Positionsnamen: `=SUMME(B2:B4)`.

  Fügt jemand eine Zeile über Zeile 2 ein, rutscht der Inhalt nach unten.
  Die Formel muss mitrutschen, sonst summiert sie danach über andere Zahlen
  als vorher — und zwar ohne dass irgendwo etwas rot wird.

  ---------------------------------------------------------------
  Die Regel, an der alles hängt
  ---------------------------------------------------------------

  **Nur das Gerät, das einfügt, schreibt um.** Der Aufruf gehört in dieselbe
  `doc.transact` wie das Einfügen und reist als ein Paket.

  Würde stattdessen jeder Browser auf `doc.on("update")` hin umschreiben,
  liefe das bei zwei Teilnehmern zweimal: `=SUMME(B2:B4)` würde erst zu
  `B3:B5` und dann zu `B4:B6`. Die Summe wäre falsch, das Blatt sähe heil
  aus, und niemand käme darauf, warum. Das ist der gefährlichste Fehler in
  diesem ganzen Bereich.

  ---------------------------------------------------------------
  Das Dollarzeichen hilft hier NICHT
  ---------------------------------------------------------------

  In Excel ist `$` eine Regel fürs KOPIEREN, nicht fürs Einfügen. `B$6`
  rutscht beim Einfügen einer Zeile genauso auf `B$7` — der Bezug zeigt
  weiterhin auf dieselbe Zahl, und genau das will man.

  Wer `$` „intuitiv" als Einfüge-Sperre baut, verrechnet jede Summe im
  Blatt, in der ein fester Bezug vorkommt. Deshalb steht es hier
  ausdrücklich: Beim Verschieben wird `$` gelesen und wieder geschrieben,
  aber es ändert nichts.

  ---------------------------------------------------------------
  Warum auf dem Baustein-Strom und nicht auf dem Baum
  ---------------------------------------------------------------

  Aus dem Baum ließe sich die Formel nur neu schreiben — und dabei gingen
  Leerzeichen, Klammerung und Groß-/Kleinschreibung des Nutzers verloren.
  Aus `=SUMME( a1 : B4 )` würde `=SUMME(A1:B4)`. Das ist keine Verbesserung,
  sondern eine fremde Hand in einem Text, den jemand selbst geschrieben hat.

  Auf dem Baustein-Strom wird nur ersetzt, was ein Bezug ist. Alles andere
  bleibt Zeichen für Zeichen stehen.
*/

/*
  Eine Formel an eine Änderung der Struktur anpassen.

      achse   "zeile" | "spalte"
      art     "eingefuegt" | "geloescht"
      stelle  die Stelle, an der eingefügt oder gelöscht wurde (ab null)

  Bei `eingefuegt` rutscht alles ab `stelle` um eins weiter.
  Bei `geloescht` rutscht alles NACH `stelle` um eins zurück — und ein
  Bezug, der genau auf die gelöschte Zeile zeigte, wird zu `#BEZUG!`.
*/
export function verschiebeFormel(text, { achse, art, stelle }) {
  return schreibeUm(text, (bezug) => {
    const wert = achse === "zeile" ? bezug.zeile : bezug.spalte;

    if (art === "eingefuegt") {
      if (wert < stelle) return bezug;
      return mitWert(bezug, achse, wert + 1);
    }

    if (art === "geloescht") {
      /*
        Der Bezug zeigte genau auf das Gelöschte. Es gibt nichts mehr, worauf
        er zeigen könnte — jede andere Wahl wäre geraten, und geraten wird in
        einem Haushaltsantrag nicht.
      */
      if (wert === stelle) return null;
      if (wert < stelle) return bezug;
      return mitWert(bezug, achse, wert - 1);
    }

    return bezug;
  });
}

/*
  Eine Formel beim Kopieren versetzen.

  Hier — und NUR hier — wirkt das Dollarzeichen: Ein festgehaltener Teil
  bleibt stehen, ein freier wandert mit.

  Das `$` steht VOR dem, was es festhält. `B$6` hält also die ZEILE fest,
  nicht die Spalte — wer die Spalte festhalten will, schreibt `$B6`. Genau
  dafür ist die Anteilsspalte eines Haushaltsantrags der Musterfall:

      C2 enthält  =B2/B$6      Ansatz geteilt durch die Summe in B6
      nach C3 kopiert (eine Zeile tiefer):
                  =B3/B$6      B2 wandert mit, B$6 bleibt auf Zeile 6

  Seitwärts kopiert wandert dagegen auch `B$6` — auf `C$6`. Das ist richtig
  so und wird leicht verwechselt.
*/
export function versetzeFormel(text, umSpalten, umZeilen) {
  return schreibeUm(text, (bezug) => {
    const spalte = bezug.spalteFest ? bezug.spalte : bezug.spalte + umSpalten;
    const zeile = bezug.zeileFest ? bezug.zeile : bezug.zeile + umZeilen;
    if (spalte < 0 || zeile < 0) return null;
    return { ...bezug, spalte, zeile };
  });
}

function mitWert(bezug, achse, wert) {
  return achse === "zeile" ? { ...bezug, zeile: wert } : { ...bezug, spalte: wert };
}

/*
  Der gemeinsame Weg: jeden Bezug durch `wandle` schicken, alles andere
  unverändert lassen.

  `wandle` gibt `null` zurück, wenn es den Bezug nicht mehr gibt — dann
  steht `#BEZUG!` im Text. Das ist Absicht und keine Notlösung: Der Fehler
  soll IN der Formel stehen, damit man beim Anklicken der Zelle sieht, was
  kaputtging.
*/
function schreibeUm(text, wandle) {
  if (!istFormel(text)) return text;

  const teile = zerlege(text);
  const neu = teile
    .map((teil) => {
      if (teil.art !== "bezug") return teil.roh;
      const gewandelt = wandle(teil.bezug);
      return gewandelt ? schreibBezug(gewandelt) : FEHLER.BEZUG;
    })
    .join("");

  return `=${neu}`;
}
